import type { JournalArticle } from "./journal-types";

/** Clusters: bagage, kinderen en toegankelijkheid + timing en reserveren. */
export const PRAKTISCH_ARTICLES: JournalArticle[] = [
  {
    slug: "met-kinderen-in-de-taxi",
    title: "Met kinderen in de taxi: wat moet u weten?",
    seoTitle: "Kinderzitje in de taxi: wat zijn de regels? | Taxiritje.nl",
    description:
      "Is een kinderzitje verplicht in de taxi? Lees de regels, wat u zelf mag meenemen en hoe u een taxi met kinderen reserveert.",
    category: "bagage-en-toegankelijkheid",
    intent: "Praktisch: ouders willen weten hoe taxivervoer met kinderen werkt.",
    primaryKeyword: "met kinderen in de taxi",
    published: "2026-08-14",
    readingMinutes: 4,
    intro:
      "Met kinderen reizen vraagt om iets meer voorbereiding: ruimte voor een wagen, een veilige plek in de auto en een planning die tegen een stootje kan.",
    sections: [
      {
        heading: "Is een kinderzitje verplicht in de taxi?",
        paragraphs: [
          "Kort antwoord: voor taxi's gelden andere regels dan voor uw eigen auto, waardoor een kinderzitje niet in alle gevallen verplicht is. Wilt u uw kind in een vertrouwd zitje vervoeren, dan neemt u het eigen zitje mee en geeft u dit bij het reserveren door. Wij rekenen daar niets extra voor.",
          "Voor het vervoeren van kinderen in een auto gelden wettelijke regels, waarbij voor taxi's afwijkende bepalingen bestaan. Omdat deze regels kunnen wijzigen, raadpleegt u het beste de actuele informatie van de Rijksoverheid. Wilt u zekerheid over een vertrouwd zitje, dan kunt u uw eigen kinderzitje meenemen; geef dat wel bij het boeken door.",
        ],
      },
      {
        heading: "Kinderwagen en bagage",
        paragraphs: [
          "Een kinderwagen telt mee als bagage en bepaalt samen met koffers welk voertuigtype nodig is. Geef daarom bij het reserveren apart aan hoeveel grote en kleine bagagestukken meegaan en of er een wagen bij zit.",
        ],
        bullets: [
          "Opvouwbare wagens nemen minder ruimte in",
          "Houd een tas met eten, drinken en spelletjes bij de hand",
          "Plan bij jonge kinderen extra tijd voor instappen",
        ],
      },
      {
        heading: "Een rustige rit",
        paragraphs: [
          "Een rit van deur tot deur voorkomt overstappen, wachten op perrons en sjouwen met bagage. Bij vroege vertrektijden slapen kinderen in de auto vaak nog even door, wat de rest van de reisdag merkbaar makkelijker maakt.",
        ],
      },
    ],
    conclusion:
      "Geef vooraf door wat u meeneemt, controleer de actuele regels rond kinderzitjes en plan iets ruimer dan gebruikelijk. Dan is de rit het rustigste deel van de dag.",
    faq: [
      {
        q: "Moet ik zelf een kinderzitje meenemen?",
        a: "Voor taxi's gelden andere regels dan voor gewone auto's. Wilt u zeker weten dat uw kind in een eigen, vertrouwd zitje reist, neem het dan mee en geef dit bij het boeken door.",
      },
      {
        q: "Past een kinderwagen mee in de auto?",
        a: "Meestal wel, maar het gaat ten koste van de bagageruimte. Vermeld de wagen bij het boeken, zodat het juiste voertuigtype wordt ingezet.",
      },
    ],
    links: [
      { label: "Taxi naar het vliegveld met kinderen", to: "/journal/taxi-naar-het-vliegveld-met-kinderen" },
      { label: "Taxi boeken", to: "/boeken" },
    ],
    sources: [
      {
        label: "Kinderzitjes en veiligheid in de auto (Rijksoverheid)",
        url: "https://www.rijksoverheid.nl/themas/verkeer-en-vervoer/verkeersveiligheid",
      },
    ],
  },
  {
    slug: "reizen-als-u-slecht-ter-been-bent",
    title: "Reizen als u slecht ter been bent: waarom een taxi helpt",
    seoTitle: "Slecht ter been? Zo helpt een taxi bij uw reis | Taxiritje.nl",
    description:
      "Minder mobiel of met een rollator of rolstoel onderweg? Lees hoe u een taxirit voorbereidt, wat u vooraf doorgeeft en waar u op let.",
    category: "bagage-en-toegankelijkheid",
    intent: "Praktisch/toegankelijkheid: reizen met beperkte mobiliteit.",
    primaryKeyword: "taxi slecht ter been",
    published: "2026-08-15",
    readingMinutes: 4,
    intro:
      "Voor wie moeilijk loopt, zit het probleem zelden in de rit zelf, maar in de meters ervoor en erna: de loopafstand naar een halte, de trap naar een perron en het wachten. Een rit van deur tot deur haalt die obstakels weg.",
    sections: [
      {
        heading: "Geef vooraf door wat u nodig heeft",
        paragraphs: [
          "Hoe meer bekend is voordat de rit begint, hoe soepeler het gaat. Vermeld bij het boeken of u een rollator, krukken of een opvouwbare rolstoel meeneemt, en of u hulp nodig heeft bij het in- of uitstappen. Zo kan het juiste voertuig worden ingepland.",
        ],
        bullets: [
          "Hulpmiddel: rollator, krukken of opvouwbare rolstoel",
          "Instaphoogte: een hogere instap is voor veel mensen prettiger",
          "Loopafstand: geef aan waar u precies wilt in- en uitstappen",
        ],
      },
      {
        heading: "Rolstoelvervoer is niet hetzelfde als een gewone rit",
        paragraphs: [
          "Een opvouwbare rolstoel kan meestal mee in de bagageruimte. Vervoer waarbij iemand in een elektrische of niet-opvouwbare rolstoel blijft zitten, vereist een speciaal ingericht voertuig met oprijplaat en vastzetsysteem. Neem daarvoor altijd vooraf contact op, zodat duidelijk is wat mogelijk is.",
        ],
      },
      {
        heading: "Plan met marge",
        paragraphs: [
          "Rustig instappen, bagage laden en een korte loopafstand overbruggen kost tijd. Reserveer daarom een ophaaltijd met ruimte, zeker bij een medische afspraak of een vlucht.",
        ],
      },
    ],
    conclusion:
      "Beschrijf uw situatie vooraf zo concreet mogelijk en plan extra tijd in. Voor niet-opvouwbaar rolstoelvervoer is speciaal ingericht vervoer nodig; neem daarover eerst contact op.",
    faq: [
      {
        q: "Kan een rolstoel mee in de taxi?",
        a: "Een opvouwbare rolstoel gaat doorgaans mee in de bagageruimte. Voor vervoer waarbij u in de rolstoel blijft zitten is een speciaal voertuig nodig; overleg dit vooraf.",
      },
      {
        q: "Kan ik hulp krijgen bij het instappen?",
        a: "Geef bij het boeken aan dat u hulp nodig heeft, dan kan daar bij de planning rekening mee worden gehouden.",
      },
    ],
    links: [
      { label: "Met de taxi naar het ziekenhuis", to: "/journal/taxi-naar-het-ziekenhuis" },
      { label: "Neem contact op", to: "/contact" },
      { label: "Taxi boeken", to: "/boeken" },
    ],
  },
  {
    slug: "met-huisdieren-in-de-taxi",
    title: "Met huisdieren in de taxi: hoe pakt u dat aan?",
    seoTitle: "Met huisdieren in de taxi: hoe pakt u dat aan? | Taxiritje.nl",
    description:
      "Een hond of kat mee in de taxi? Lees hoe u dit vooraf regelt, welke vervoersbox handig is en hoe u de rit voor uw dier rustig houdt.",
    category: "bagage-en-toegankelijkheid",
    intent: "Praktisch: reizen met een huisdier.",
    primaryKeyword: "huisdier mee in de taxi",
    published: "2026-08-16",
    readingMinutes: 3,
    intro:
      "Een rit naar de dierenarts, een verhuizing of een vakantie: er zijn genoeg momenten waarop uw huisdier mee moet. Het belangrijkste is dat u dit vooraf meldt, zodat er geen verrassingen zijn bij de deur.",
    sections: [
      {
        heading: "Meld het huisdier bij het boeken",
        paragraphs: [
          "Of een dier mee kan, hangt af van het voertuig en de rit. Geef daarom bij het reserveren door welk dier meegaat, hoe groot het is en of het in een bench of reismand reist. Zo wordt de rit met die informatie ingepland en weet u vooraf waar u aan toe bent.",
        ],
      },
      {
        heading: "Een reismand of bench is bijna altijd verstandig",
        paragraphs: [
          "Een vervoersbox is veiliger voor het dier én rustiger voor de rit. Katten en kleine honden reizen het prettigst in een gesloten mand; grotere honden kunnen met een veiligheidstuig of in een bench in de bagageruimte.",
        ],
        bullets: [
          "Neem een deken mee die naar thuis ruikt",
          "Geef vlak voor vertrek geen grote maaltijd",
          "Houd bij lange ritten water bij de hand",
        ],
      },
      {
        heading: "Assistentiehonden",
        paragraphs: [
          "Een assistentiehond die iemand met een beperking begeleidt, heeft een andere status dan een huisdier. Meld ook hier bij het boeken dat de hond meereist, zodat het voertuig daarop is voorbereid.",
        ],
      },
    ],
    conclusion:
      "Meld uw huisdier altijd vooraf en reis bij voorkeur met een mand of bench. Dan verloopt de rit rustig voor uw dier, voor u en voor de chauffeur.",
    faq: [
      {
        q: "Mag mijn hond zomaar mee in de taxi?",
        a: "Meld het altijd vooraf bij het boeken. Of en hoe het dier mee kan, hangt af van de grootte van het dier en het voertuig.",
      },
    ],
    links: [
      { label: "Taxi boeken", to: "/boeken" },
      { label: "Neem contact op", to: "/contact" },
    ],
  },
  {
    slug: "taxibus-met-meerdere-personen",
    title: "Taxibus met meerdere personen: wanneer is dat voordeliger?",
    seoTitle: "Taxibus met meerdere personen: wanneer voordeliger? | Taxiritje.nl",
    description:
      "Vanaf hoeveel personen is een taxibus slim? Lees hoe ruimte, bagage en de prijs per persoon samenhangen bij groepsvervoer.",
    category: "bagage-en-toegankelijkheid",
    intent: "Vergelijkend/praktisch: keuze tussen personenauto en taxibus.",
    primaryKeyword: "taxibus meerdere personen",
    published: "2026-08-18",
    readingMinutes: 4,
    intro:
      "Een personenauto biedt plaats aan maximaal vier passagiers. Zodra u met vijf of meer personen reist, of veel bagage meeneemt, is een bus nodig — en dat verandert de rekensom minder dan u denkt.",
    sections: [
      {
        heading: "Wanneer is een bus nodig?",
        paragraphs: [
          "Twee factoren bepalen het voertuigtype: het aantal inzittenden en de hoeveelheid bagage. Vijf of meer personen betekent altijd een bus. Maar ook met vier personen kan een bus nodig zijn, bijvoorbeeld wanneer iedereen een grote koffer meeneemt.",
        ],
        bullets: [
          "5 tot 8 personen: bus",
          "4 personen met veel grote koffers: vaak ook een bus",
          "Kinderwagen, rolstoel of sportuitrusting: meld dit apart",
        ],
      },
      {
        heading: "De prijs per persoon",
        paragraphs: [
          "Een bus heeft een ander tarief dan een personenauto, maar u deelt dat bedrag met meer mensen. Deel de ritprijs door het aantal inzittenden en vergelijk dat met wat losse vervoerbewijzen zouden kosten; bij zes tot acht personen valt dat bijna altijd gunstig uit.",
        ],
      },
      {
        heading: "Eén bus of twee auto's?",
        paragraphs: [
          "Eén voertuig is overzichtelijker: iedereen vertrekt en arriveert samen. Twee auto's kunnen praktischer zijn als de groep vanaf verschillende adressen vertrekt, omdat u dan geen extra kilometers voor tussenstops maakt.",
        ],
      },
    ],
    conclusion:
      "Kies een bus vanaf vijf personen of bij veel bagage, en reken de prijs per persoon uit voordat u vergelijkt. Geef altijd het echte aantal personen en bagagestukken op, dan staat het juiste voertuig klaar.",
    faq: [
      {
        q: "Hoeveel personen kunnen er mee in een taxibus?",
        a: "Een bus is doorgaans geschikt voor vijf tot acht passagiers, afhankelijk van de bagage die meegaat.",
      },
      {
        q: "Wordt een bus automatisch gekozen bij veel bagage?",
        a: "Bij het boeken geeft u het aantal grote en kleine bagagestukken op; op basis daarvan wordt een passend voertuigtype voorgesteld voordat u bevestigt.",
      },
    ],
    links: [
      { label: "Reizen met een groep", to: "/journal/reizen-met-een-groep" },
      { label: "Taxi boeken", to: "/boeken" },
    ],
  },
  {
    slug: "hoe-lang-van-tevoren-taxi-reserveren",
    title: "Hoe lang van tevoren moet u een taxi reserveren?",
    seoTitle: "Hoe lang van tevoren een taxi reserveren? | Taxiritje.nl",
    description:
      "Wanneer boekt u uw taxi het beste? Richtlijnen per situatie: vroege vluchten, groepsvervoer, spitsuren, nachtritten en feestdagen.",
    category: "timing-en-reserveren",
    intent: "Informatief: bezoeker wil weten wanneer te reserveren.",
    primaryKeyword: "hoe lang van tevoren taxi reserveren",
    published: "2026-08-12",
    readingMinutes: 4,
    intro:
      "Boek online minimaal 3 uur voor het ophalen. Wij adviseren minstens 24 uur vooraf te reserveren; dit is advies, geen extra boekingsvoorwaarde. Voor vroege vluchten, groepen en drukke periodes boekt u zodra uw plannen bekend zijn.",
    sections: [
      {
        heading: "Richtlijnen per situatie",
        paragraphs: [
          "Gebruik onderstaande tabel als leidraad. Bij twijfel geldt: eerder boeken kost niets extra en geeft meer zekerheid over de gewenste tijd en het juiste voertuig.",
        ],
        table: {
          columns: ["Situatie", "Aanbevolen reserveringsmoment"],
          rows: [
            ["Gewone rit overdag", "Minimaal een dag vooruit"],
            ["Vroege ochtendvlucht", "Zodra uw vlucht bekend is"],
            ["Nachtrit", "Ruim vooraf, minstens enkele dagen"],
            ["Groep of taxibus", "Ruim vooraf, zeker in het weekend"],
            ["Feestdagen en vakantiepieken", "Zo vroeg mogelijk"],
            ["Evenement of concert", "Samen met het kaartje, ook de terugrit"],
          ],
        },
      },
      {
        heading: "Waarom vroeg boeken helpt",
        paragraphs: [
          "Bij een vooraf geboekte rit staan het ophaalmoment, het voertuigtype en de prijs vast. U hoeft op de dag zelf niets meer te regelen en loopt niet het risico dat het gewenste tijdslot al vol zit.",
        ],
      },
      {
        heading: "Kunt u later nog iets wijzigen?",
        paragraphs: [
          "Wijzigen kan online tot 24 uur voor het ophaalmoment: datum, tijd, adressen, vluchtnummer, bagage of het aantal inzittenden. Binnen die 24 uur loopt een wijziging telefonisch, omdat de planning dan al definitief is. Wordt de rit door een wijziging duurder of goedkoper, dan ziet u dat verschil voordat u bevestigt.",
        ],
      },
    ],
    conclusion:
      "Boek gewone ritten minstens een dag vooruit en bijzondere ritten zodra u de tijd weet. Wijzigen kan online tot 24 uur voor vertrek, dus vroeg reserveren kost u geen flexibiliteit.",
    faq: [
      {
        q: "Kan ik ook op dezelfde dag nog een taxi boeken?",
        a: "Ja, als het ophalen minimaal 3 uur later is en de planning beschikbaarheid heeft. Heeft u eerder een taxi nodig, bel dan 085 060 7086. Wij adviseren minstens 24 uur vooraf te reserveren, maar dat is geen boekingsvoorwaarde.",
      },
      {
        q: "Tot wanneer kan ik mijn rit wijzigen?",
        a: "Online tot 24 uur voor het ophaalmoment. Daarbinnen regelt u het telefonisch.",
      },
    ],
    links: [
      { label: "Reserveer uw rit", to: "/boeken" },
      { label: "Rit wijzigen of annuleren", to: "/journal/rit-wijzigen-of-annuleren" },
      { label: "Annuleringsbeleid", to: "/annuleringsbeleid" },
    ],
  },
  {
    slug: "taxi-in-de-nacht",
    title: "Taxi in de nacht: waar moet u rekening mee houden?",
    seoTitle: "Taxi in de nacht: waar moet u op letten? | Taxiritje.nl",
    description:
      "Nachtrit plannen? Reserveer minimaal drie uur vooraf en rijd met een vaste prijs zonder nachttarief. Lees hoe u veilig en op tijd aankomt.",
    category: "timing-en-reserveren",
    intent: "Praktisch: vervoer regelen in de nachtelijke uren.",
    primaryKeyword: "taxi in de nacht",
    published: "2026-08-13",
    readingMinutes: 3,
    intro:
      "In de nacht is de weg rustig, maar het aanbod aan vervoer klein. Het openbaar vervoer rijdt beperkt of niet, en spontaan een taxi vinden is buiten de uitgaanscentra lastig. Vooraf regelen is daarom het halve werk.",
    sections: [
      {
        heading: "Reserveer vooraf, niet ter plekke",
        paragraphs: [
          "Een nachtrit die al is ingepland, staat op tijd voor de deur. Wie pas 's nachts gaat zoeken, is afhankelijk van wat er op dat moment beschikbaar is — en dat is per definitie onzeker.",
        ],
      },
      {
        heading: "Voordelen van rijden in de nacht",
        paragraphs: [
          "Zonder spits is de reistijd korter en beter te voorspellen. Vooral bij ritten naar een luchthaven betekent dat een strakkere planning en minder marge voor files.",
        ],
      },
      {
        heading: "Praktische aandachtspunten",
        paragraphs: ["Een paar kleine dingen maken een nachtrit merkbaar rustiger."],
        bullets: [
          "Zorg dat uw telefoon opgeladen is en het geluid aan staat",
          "Controleer het ophaaladres inclusief huisnummer",
          "Wacht binnen tot het voertuig er is",
          "Zet bagage van tevoren klaar",
        ],
      },
      {
        heading: "Een nachtrit boeken",
        paragraphs: [
          "U boekt een nachtrit precies zoals een rit op de dag: ophaaladres, bestemming, datum en tijd invullen, de vaste prijs bekijken en online afrekenen. Er komt geen nachttarief bij en de prijs verandert niet als de rit langer duurt dan verwacht. Reserveer minimaal drie uur voor het ophalen, zodat wij een chauffeur kunnen vastleggen.",
          "Nachtritten naar de luchthaven zijn onze meest geboekte nachtelijke rit. De vaste prijs voor uw eigen adres berekent u op de prijzenpagina's; die bedragen komen uit dezelfde berekening als het afrekenen.",
        ],
      },
    ],
    conclusion:
      "Reserveer nachtritten ruim van tevoren, controleer uw adresgegevens en houd uw telefoon bij de hand. Dan is de nacht juist het rustigste moment om te reizen.",
    faq: [
      {
        q: "Rijdt het openbaar vervoer 's nachts?",
        a: "Op sommige trajecten rijden nachtbussen of nachttreinen, maar met een lage frequentie. Controleer dit vooraf in de reisplanner.",
      },
      {
        q: "Betaalt u 's nachts een nachttarief?",
        a: "Nee. Wij werken zonder taximeter en zonder nachttarief: de vaste prijs die u bij het boeken ziet, geldt ook voor een rit midden in de nacht.",
      },
    ],
    links: [
      { label: "Taxi bij een vroege vlucht", to: "/journal/taxi-naar-het-vliegveld-bij-een-vroege-vlucht" },
      { label: "Taxi boeken", to: "/boeken" },
      { label: "Taxi naar Schiphol", to: "/taxi-schiphol" },
      { label: "Prijzen naar Schiphol", to: "/prijzen/schiphol" },
    ],
  },
  {
    slug: "taxi-tijdens-de-spits",
    title: "Taxi tijdens de spits: zo houdt u uw planning kloppend",
    seoTitle: "Taxi tijdens de spits: zo plant u het slim | Taxiritje.nl",
    description:
      "Reizen in de ochtend- of avondspits? Lees hoeveel marge u aanhoudt, waarom een vaste prijs dan prettig is en hoe u vertraging voorkomt.",
    category: "timing-en-reserveren",
    intent: "Praktisch: reizen tijdens drukke uren.",
    primaryKeyword: "taxi tijdens de spits",
    published: "2026-08-16",
    readingMinutes: 3,
    intro:
      "Tussen ruwweg 07:00 en 09:30 uur en tussen 16:00 en 18:30 uur is de reistijd op veel wegen aanzienlijk langer. Dat is geen reden om de taxi te mijden, wel om anders te plannen.",
    sections: [
      {
        heading: "Bouw marge in",
        paragraphs: [
          "Reken tijdens de spits op een reistijd die aanzienlijk langer kan zijn dan buiten de spits. Bij belangrijke afspraken of een vlucht is een extra marge van twintig tot dertig minuten geen luxe.",
        ],
      },
      {
        heading: "Waarom een vaste prijs in de spits prettig is",
        paragraphs: [
          "Bij een taximeter tikt stilstaan in de file gewoon door. Bij een vaste prijs staat het bedrag vast op het moment van boeken, ongeacht drukte of omleidingen. Juist tijdens de spits maakt dat verschil.",
        ],
      },
      {
        heading: "Kies waar mogelijk een ander tijdstip",
        paragraphs: [
          "Kunt u schuiven, vertrek dan net vóór of ná de piek. Een half uur eerder vertrekken scheelt op drukke corridors vaak meer reistijd dan u zou verwachten.",
        ],
      },
    ],
    conclusion:
      "Plan tijdens de spits met extra marge, kies waar mogelijk een rustiger tijdstip en boek met een vaste prijs, zodat vertraging uw kosten niet beïnvloedt.",
    faq: [
      {
        q: "Betaal ik meer als we in de file staan?",
        a: "Niet bij een rit met een vaste prijs: het bedrag staat vast zodra u boekt. Bij een taximeter loopt het bedrag tijdens stilstaan wel door.",
      },
    ],
    links: [
      { label: "Vaste prijs of taxameter", to: "/journal/vaste-prijs-versus-taxameter" },
      { label: "Taxi boeken", to: "/boeken" },
    ],
  },
  {
    slug: "vertraging-van-trein-of-vlucht",
    title: "Wat als uw vlucht of trein vertraging heeft?",
    seoTitle: "Vertraagde vlucht of trein: wat doet u met uw taxi? | Taxiritje.nl",
    description:
      "Vertraging bij uw vlucht of trein? Lees hoe u uw taxirit aanpast, waarom een vluchtnummer belangrijk is en wat u het beste doorgeeft.",
    category: "timing-en-reserveren",
    intent: "Probleemoplossend: reiziger heeft te maken met vertraging.",
    primaryKeyword: "vlucht vertraging taxi",
    published: "2026-08-19",
    readingMinutes: 3,
    intro:
      "Vertraging is vervelend, maar hoeft uw vervoer niet in de war te schoppen — mits de juiste informatie bekend is en u op tijd laat weten wat er verandert.",
    sections: [
      {
        heading: "Bij een vertraagde vlucht",
        paragraphs: [
          "Daarom wordt bij luchthavenritten altijd om een vluchtnummer gevraagd. Bij de terugreis wordt gepland op de landingstijd in plaats van op een vast ophaalmoment, zodat de rit aansluit op het moment waarop u daadwerkelijk aankomt. Verandert er iets ingrijpends, bijvoorbeeld een geannuleerde vlucht, neem dan zo snel mogelijk contact op.",
        ],
      },
      {
        heading: "Bij een vertraagde trein",
        paragraphs: [
          "Wordt u bij een station opgehaald en loopt uw trein vertraging op, laat dat dan zo vroeg mogelijk weten. Bij grote vertraging is het soms praktischer om de rit naar een later tijdstip te verplaatsen dan te laten wachten.",
        ],
      },
      {
        heading: "Wat u het beste doorgeeft",
        paragraphs: ["Hoe concreter uw melding, hoe makkelijker de planning kan meebewegen."],
        bullets: [
          "Uw ritnummer",
          "De nieuwe verwachte aankomsttijd",
          "Of het adres of de instapplaats verandert",
        ],
      },
    ],
    conclusion:
      "Geef vertraging zo vroeg mogelijk door en zorg dat uw vluchtnummer bij de boeking bekend is. Dan schuift uw rit mee in plaats van dat u opnieuw moet beginnen.",
    faq: [
      {
        q: "Moet ik zelf melden dat mijn vlucht vertraagd is?",
        a: "Bij grote wijzigingen zoals een annulering of een sterk afwijkende landingstijd is contact opnemen altijd verstandig. Voor de terugreis wordt gepland op basis van uw vluchtnummer en landingstijd.",
      },
      {
        q: "Wat gebeurt er als mijn taxi te laat is?",
        a: "Neem in dat geval telefonisch contact op, dan wordt gekeken wat de status van de rit is en wat er nog mogelijk is.",
      },
    ],
    links: [
      { label: "Rit wijzigen of annuleren", to: "/journal/rit-wijzigen-of-annuleren" },
      { label: "Contact opnemen", to: "/contact" },
      { label: "Luchthaventaxi", to: "/luchthaventaxi" },
    ],
  },
];
